import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from 'components/ui/Button';
import { RefreshCw, Play, Clock, Mail, Check, X, Info, Users, Trash2, Download } from 'lucide-react';
import { useAutoSync } from 'contexts/AutoSyncContext';
import { offersApi, uploadsApi, teamsApi, applicationsApi, type Offer, type Team } from 'lib/api';
import { useNotifications } from 'contexts/NotificationContext';

export function AutoSyncButton({ onImportStarted }: any) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { addNotification } = useNotifications();
  
  const { 
    isSyncActive, setIsSyncActive, 
    syncInterval, setSyncInterval, 
    lastSyncTime, pendingFiles, 
    isSyncing, isConnected, 
    triggerSync, handleImportPending, handleDeletePending,
    autoSyncTeamId, setAutoSyncTeamId
  } = useAutoSync();

  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [offers, setOffers] = useState<Offer[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedOfferIds, setSelectedOfferIds] = useState<Record<string, number>>({});

  useEffect(() => {
    if (pendingFiles && pendingFiles.length > 0) {
      setSelectedOfferIds(prev => {
        const next = { ...prev };
        let hasChanges = false;
        pendingFiles.forEach(file => {
          if (file.suggested_offer_id && !next[file.id]) {
            next[file.id] = file.suggested_offer_id;
            hasChanges = true;
          }
        });
        return hasChanges ? next : prev;
      });
    }
  }, [pendingFiles]);

  useEffect(() => {
      // Fetch teams unconditionally
      const fetchTeamsData = async () => {
        try {
          const teamsRes = await teamsApi.getAll();
          setTeams(teamsRes.data.results || []);
        } catch (err) {
          console.error("Failed to fetch teams", err);
        }
      };
      fetchTeamsData();
    }, []);

    // Only fetch offers if there are pending files (so we don't load them on every mount if empty)
    const offersFetchedRef = useRef(false);
    useEffect(() => {
      if (!pendingFiles || pendingFiles.length === 0 || offersFetchedRef.current) return;
      
      const fetchOffersData = async () => {
        try {
          offersFetchedRef.current = true;
          const offersData = await offersApi.getAllPages({ light: 'true' });
          setOffers(offersData || []);
        } catch (err) {
          console.error("Failed to fetch offers", err);
          offersFetchedRef.current = false;
        }
      };
      
      fetchOffersData();
    }, [pendingFiles]);
  useEffect(() => {
    const handleGlobalEvent = (e: any) => {
      const { batchId } = e.detail || {};
      if (batchId) {
        onImportStarted?.({ batchId, totalFiles: 0, batchName: `Auto batch ${batchId}` });
      }
    };
    window.addEventListener('selektia_auto_sync_imported', handleGlobalEvent);
    return () => window.removeEventListener('selektia_auto_sync_imported', handleGlobalEvent);
  }, [onImportStarted]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectOffer = (fileId: string, offerId: number) => {
    setSelectedOfferIds(prev => ({ ...prev, [fileId]: offerId }));
  };

  const handleConfirmAssignment = async (file: any) => {
    const offerId = selectedOfferIds[file.id];
    if (!offerId || !file.candidate_id) return;
    
    try {
      await applicationsApi.create({
        candidate: file.candidate_id,
        offer: offerId
      });

      await uploadsApi.logAutoSyncAction({
        action: 'assign_offer',
        candidate_id: file.candidate_id,
        offer_id: offerId,
        filename: file.filename,
        sender: file.sender,
        subject: file.subject,
        details: `Assigned file ${file.filename} to offer ${offerId}`,
        status: 'success'
      });

      addNotification({
        type: 'success',
        title: t('common.success', 'Success'),
        message: t('candidates.emailImport.assignSuccess', 'Candidate successfully linked to offer'),
      });
      
      setSelectedOfferIds(prev => {
        const next = { ...prev };
        delete next[file.id];
        return next;
      });

      if (handleDeletePending) {
         handleDeletePending(file, false);
      }
    } catch (err) {
      console.error("Failed to confirm assignment", err);
      addNotification({
        type: 'error',
        title: t('common.error', 'Error'),
        message: t('candidates.emailImport.assignError', 'Failed to link candidate to offer'),
      });
    }
  };

  const handleRejectAssignment = async (file: any) => {
    try {
      await uploadsApi.logAutoSyncAction({
        action: 'reject_candidate',
        candidate_id: file.candidate_id,
        filename: file.filename,
        sender: file.sender,
        subject: file.subject,
        details: `Rejected assignment for file ${file.filename}`,
        status: 'success'
      });
      if (handleDeletePending) {
         handleDeletePending(file, false);
      }
    } catch (err) {
      console.error("Failed to reject assignment", err);
    }
  };

  const handleButtonClick = () => {
    if (!isConnected) {
      navigate('/auto-sync');
    } else {
      setShowDropdown(!showDropdown);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <Button 
        variant="outline" 
        onClick={handleButtonClick}
        size="sm"
        className={isSyncActive ? 'border-primary text-primary bg-primary/5' : ''}
      >
        <RefreshCw className={`h-4 w-4 mr-2 ${isSyncing ? 'animate-spin' : (isSyncActive ? 'animate-[spin_3s_linear_infinite]' : '')}`} />
        <span>{t('candidates.sync.title', 'Auto Sync')}</span>
        {isConnected && pendingFiles && pendingFiles.length > 0 && (
          <span className="ml-2 flex h-5 w-5 items-center justify-center rounded-full bg-amber-100 text-[10px] font-bold text-amber-800">
            {pendingFiles.length}
          </span>
        )}
      </Button>

      {isConnected && showDropdown && (
        <div className="absolute right-0 mt-2 w-96 rounded-md border bg-card shadow-lg z-50 overflow-hidden text-sm flex flex-col">
          <div className="p-3 border-b bg-muted/30 shrink-0">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold flex items-center gap-2">
                <RefreshCw className="h-4 w-4" />
                {t('candidates.sync.settings', 'Sync Settings')}
                 <div className="relative group flex items-center">
                    <Info className="h-4 w-4 text-muted-foreground/60 hover:text-foreground transition-colors cursor-help" />
                    <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 hidden group-hover:block w-64 p-2 bg-slate-800 text-white text-xs font-normal text-center rounded shadow-lg z-50">
                      {t('candidates.emailImport.autoSyncInfo')}
                      <div className="absolute -top-1 left-1/2 -translate-x-1/2 border-4 border-transparent border-b-slate-800" />
                    </div>
                  </div>
              </h4>
              <button
                onClick={() => setIsSyncActive(!isSyncActive)}
                className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background ${isSyncActive ? 'bg-primary' : 'bg-muted'}`}
                role="switch"
                aria-checked={isSyncActive}
              >
                <span aria-hidden="true" className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${isSyncActive ? 'translate-x-4' : 'translate-x-0'}`} />
              </button>
            </div>
            
            <div className="flex items-center gap-2 mb-3">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">Every</span>
              <input 
                type="number" 
                min={1} max={60} 
                value={syncInterval}
                disabled={isSyncActive}
                onChange={e => setSyncInterval(Number(e.target.value) || 5)}
                className="w-16 h-7 rounded border-input px-2 text-sm text-center bg-background disabled:opacity-50 disabled:bg-muted disabled:cursor-not-allowed"
              />
              <span className="text-muted-foreground">min</span>
            </div>

            <div className="flex items-center gap-2 mb-3">
              <Users className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground hidden lg:inline">Group</span>
              <select
                value={autoSyncTeamId || ''}
                disabled={isSyncActive}
                onChange={(e) => setAutoSyncTeamId(Number(e.target.value) || null)}
                className="flex-1 h-7 rounded border-input px-2 text-xs bg-background border disabled:opacity-50 disabled:bg-muted disabled:cursor-not-allowed"
              >
                <option value="">{t('candidates.sync.allGroups', 'All Groups')}</option>
                {teams.map(team => (
                  <option key={team.id} value={team.id}>{team.name}</option>
                ))}
              </select>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">
                  {lastSyncTime ? `${t('candidates.sync.lastSync', 'Last sync')}: ${lastSyncTime}` : t('candidates.sync.neverSynced', 'Not synced yet')}
                </span>
                <Button size="sm" variant="secondary" className="h-7 text-xs" onClick={() => triggerSync()} disabled={isSyncing}>
                  <Play className={`h-3 w-3 mr-1 ${isSyncing ? 'animate-pulse' : ''}`} /> {isSyncing ? t('candidates.sync.syncing', 'Syncing...') : t('candidates.sync.syncNow', 'Sync Now')}
              </Button>
            </div>
          </div>

          <div className="max-h-80 overflow-y-auto w-full">
            {!pendingFiles || pendingFiles.length === 0 ? (
              <div className="p-4 text-center text-muted-foreground text-xs flex flex-col items-center">
                <Mail className="h-8 w-8 mb-2 opacity-20" />
                {t('candidates.emailImport.noPendingEmails')}
              </div>
            ) : (
              <ul className="divide-y w-full">
                {pendingFiles.map((file: any) => (
                  <li key={file.id} className="p-3 hover:bg-muted/30 group flex flex-col gap-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-xs truncate flex items-center gap-1" title={file.filename}>
                          {file.filename}
                          {file.status === 'importing' && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[8px] font-medium bg-blue-100 text-blue-800 shrink-0">
                              <RefreshCw className="h-2 w-2 mr-1 animate-spin inline" /> {t('candidates.emailImport.inProgress')}
                            </span>
                          )}
                          {file.status === 'imported' && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[8px] font-medium bg-green-100 text-green-800 shrink-0">
                              {t('candidates.emailImport.importedStatus')}
                            </span>
                          )}
                          {file.status === 'error' && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[8px] font-medium bg-red-100 text-red-800 shrink-0">
                              {t('candidates.emailImport.errorStatus')}
                            </span>
                          )}
                          {file.is_application === false && !file.is_duplicate && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[8px] font-medium bg-amber-100 text-amber-800 shrink-0">
                              {t('candidates.sync.nonCvMail', 'Mail normal détecté')}
                            </span>
                          )}
                        </p>
                        <p className="text-[10px] text-muted-foreground truncate leading-tight mt-0.5">
                          {file.sender} • {file.subject}
                        </p>
                      </div>
                      
                      {file.status === 'imported' && file.candidate_id ? (
                        <div className="flex shrink-0 items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity h-full pt-1">
                           <Button
                              variant="ghost"
                              size="sm"
                              title="Confirm Assignment"
                              className="h-6 w-6 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
                              onClick={() => handleConfirmAssignment(file)}
                              disabled={!selectedOfferIds[file.id]}
                           >
                              <Check className="h-4 w-4" />
                           </Button>
                           <Button
                              variant="ghost"
                              size="sm"
                              title="Reject"
                              className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleRejectAssignment(file)}
                           >
                              <X className="h-4 w-4" />
                           </Button>
                        </div>
                      ) : (
                        <div className="flex opacity-0 group-hover:opacity-100 transition-opacity shrink-0 items-center h-full pt-1">
                          {file.status !== 'imported' && file.status !== 'importing' && (
                            <button onClick={(e) => {
                               e.stopPropagation();
                               handleImportPending(file);
                            }} className="p-1 text-primary hover:bg-primary/10 rounded" title="Import" disabled={file.status === 'importing'}>
                              <Download className="h-3.5 w-3.5" />
                            </button>
                          )}
                          <button onClick={(e) => {
                             e.stopPropagation();
                             handleDeletePending(file);
                          }} className="p-1 text-red-500 hover:bg-red-50 rounded" title="Delete" disabled={file.status === 'importing'}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      )}
                    </div>
                    {/* Offer Assignment Dropdown inline */}
                    {file.status === 'imported' && file.candidate_id && (
                      <div className="mt-1">
                        <select 
                          className="w-full text-xs h-7 rounded border-input px-2 bg-background border"
                          value={selectedOfferIds[file.id] || ''}
                          onChange={(e) => handleSelectOffer(file.id, Number(e.target.value))}
                        >
                          <option value="" disabled>-- {t('candidates.sync.selectOffer', 'Select offer to assign...')} --</option>
                          {offers.map(off => (
                            <option key={off.id} value={off.id}>{off.job_title}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="border-t p-2">
            <Button className="w-full" variant="outline" onClick={() => navigate('/auto-sync')}>
              Voir les détails
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
